import { AllSameKeySetEnforcer, getLastTruthyInList, jsonCopyObject } from "..//helpers/helpers";
import { createId } from "../helpers/helpers";
import { JoiFile } from "./files.model";
import { JoiAccount } from "./accounts.data";
import { JoiAppInfo } from "./app.data";
import { SocialLink } from "./entity.shared.data";
import { JoiEvent, LabelObject } from "./events.data";
import { DropdownOption } from "./programs.data";
import { PublishInfo, PublishStatus, ShareStatus } from "./share.data";
import { SelectableLabelGroup } from "./dialog.selector.data";



export interface PublishInBriefSelection { publishCode: string, title: string, status?: PublishStatus, on?: boolean }

export interface LogisticsInBriefSelection { code: string, title: string, status?: ShareStatus, on?: boolean }


export type TemplateFont = 'fantasy' | 'arial' | 'verdana' | 'arialNarrow' | 'times' | 'georgia' | 'courier' | 'andaleMono' | 'comicSans' | 'impact' | 'stencilStd' | 'ubuntu'
export const FontNames: { [f in TemplateFont]: string } = {
  fantasy: 'Fantasy', arial: 'Arial', verdana: 'Verdana', arialNarrow: 'Arial Narrow',
  times: 'Times New Roman', georgia: 'Georgia', courier: 'Courier New', andaleMono: 'Andale Mono',
  comicSans: 'Comic Sans MS', impact: 'Impact', stencilStd: 'Stencil Std', ubuntu: 'Ubuntu'
}
export const FontCSS: { [f in TemplateFont]: string } = {
  fantasy: 'fantasy', arial: 'arial', verdana: 'Verdana', arialNarrow: 'Arial Narrow',
  times: 'Times', georgia: 'Georgia', courier: 'Courier New', andaleMono: 'Andale Mono',
  comicSans: 'Comic Sans MS', impact: 'Impact', stencilStd: 'Stencil Std', ubuntu: 'Ubuntu'
}
export const TemplateFonts = Object.keys(FontCSS).sort() as TemplateFont[]
export const FontDropdownOptions: DropdownOption<TemplateFont>[] = TemplateFonts.map(id => ({ id, title: FontNames[id], font: FontCSS[id] }))

export interface ColorTemplate {
  primary: string 
  primaryhover: string
  link: string
  contentbg: string
  contentbgoutline: string
  innercontentbg: string
  innercontentbgoutline: string
  sitebackground: string
  pagebackground: string
}
export interface JoiTemplate extends ColorTemplate {
  id: string
  name: string
  font: TemplateFont
  headerFont: TemplateFont
}

export const makeNewTemplate = (id?: string, name?: string): JoiTemplate => ({
  id: id || createId(),
  name: name || "Custom Template",
  primary: "#11b8c1",
  primaryhover: "#5A97A6",
  link: "#5A97A6",
  contentbg: "#EEF2F5",
  contentbgoutline:"#EEF2F5",
  innercontentbg: "#DDE6EC",
  innercontentbgoutline:"#DDE6EC",
  sitebackground: '#ffffff',
  pagebackground: '#ffffff',
  font: 'ubuntu',
  headerFont: 'ubuntu',
})

export const makeNewSocialTemplate = (name?: string): SocialTemplate => ({
  id: createId(),
  name: name || 'New Social Links Set',
  social: []
})

export const makeNewFooterLinks = (name?: string): FooterLinks => ({
  id: createId(),
  name: name || 'New Footer Links Set',
  links: []
})

export const getJoiTemplate = (): JoiTemplate => makeNewTemplate("joi", "Default Fonts and Colors")

export const ColorTemplateNames: AllSameKeySetEnforcer<ColorTemplate> = {
  primary: 'Action Button',
  primaryhover: "Action Hover",
  link: "Links",
  contentbg: "Content Background",
  contentbgoutline: "Content Outline",
  innercontentbg: "Inner Background",
  innercontentbgoutline: "Inner Outline",
  sitebackground: "Site Background",
  pagebackground: "Page Background"
}

export const ColorTemplateKeys: (keyof ColorTemplate)[] = (Object.keys(ColorTemplateNames) as unknown as (keyof ColorTemplate)[])


export interface SocialTemplate {
  id: string
  name: string
  social: SocialLink[]
}

export interface FooterLinks {
  id: string
  name: string
  links: JoiFile[]
}

export type BrandOption = 'header' | 'footer' | 'template' | 'social' | 'list' | 'footerLinks'
export const BrandOptionTabNames: { [BO in BrandOption]: string } = { 
  header: 'Header',
  footer: 'Footer',
  template: 'Fonts and Colors',
  social: 'Social Links',
  list: 'Event list Thumbnail',
  footerLinks: 'Footer Links',
}
export const BrandOptions = {} as { [BO in BrandOption]: BO }
Object.keys(BrandOptionTabNames).forEach(BO => BrandOptions[BO] = BO)

export type BrandFormType = 'session' | 'profiles' | 'revenue' | 'submissions' | 'briefs' | 'schedules' | 'logistics' | 'information' | 'attendees'
export const BrandFormTypeNames: { [T in BrandFormType]: string } = { 
  session: 'Session Information', profiles: 'Profiles', revenue: 'Budget Revenue Share / Quote Submission',
  submissions: 'Submissions', briefs: 'Briefs', schedules: 'On Site Schedule', logistics: 'Logistics Share', information: 'Information', attendees: 'Attendees Share'
}
export const brandFormTypesMap = {} as { [T in BrandFormType]: T }
export const brandFormTypeList: BrandFormType[] = []
Object.keys(BrandFormTypeNames).forEach(T => brandFormTypesMap[T] = T)
Object.keys(BrandFormTypeNames).forEach(T => brandFormTypeList.push(T as BrandFormType))

export interface JoiBrandImage {
  id: string
  name: string
  link: string
  mobileLink?: string
}
type BImageType = 'headers' | 'footers'
export type BrandingImageType = BImageType & keyof JoiAccount

export type BrandType = 'list' | 'forms' | 'publish' | 'app'

export const createJoiBrandImageSet = (type: BrandingImageType, name?: string): JoiBrandImage => ({
  id: createId(),
  name: name || `New ${type === 'headers' ? 'header' : type === 'footers' ? 'footer' : 'event list thumbnail'} image set`,
  link: null   
})

export interface BrandingSelectData {
  type: BrandType
  accountId?: string
  publishId?: string
  appId?: string
  eventListId?: string
  brandFormType?: BrandFormType // only for 'forms'
}

export interface BrandingData {
  template: JoiTemplate
  header: JoiBrandImage
  footer: JoiBrandImage
  social: SocialTemplate
  footerLinks: FooterLinks
}

export interface BrandingIds {  
  headerId?: string
  footerId?: string
  socialId?: string
  templateId?: string
  linksId?: string
  footerLinksId?: string
}

export interface BrandingProps extends BrandingIds {
  ignoredForms?: BrandFormType[]
}

export interface HomePageTextBlock {
  text: string
  fontSize?: number
  color?: string
}

export interface SliderImage {
  imageUrl?: string
  linkUrl?: string
}


export interface HomePageData {
  headerText?: HomePageTextBlock
  bodyText?: HomePageTextBlock
  textBlock?: HomePageTextBlock
  performerIds?: string[]
  sponsorIds?: string[]
  exhibitorIds?: string[]
  sliderImages?: SliderImage[]
}

export const createEmptyHomePageTextBlock = (): HomePageTextBlock => ({ text: '' })
export const createHomePageData = (): HomePageData => ({
  headerText: createEmptyHomePageTextBlock(),
  bodyText: createEmptyHomePageTextBlock(),
  textBlock: createEmptyHomePageTextBlock(),
  performerIds: [],
  sponsorIds: [],
  sliderImages: []
})
export const createEventListBuilderData = (): HomePageData => ({
  headerText: createEmptyHomePageTextBlock(),
  textBlock: createEmptyHomePageTextBlock(),
})



export interface HomePageEntity {
  // show boolean OR add list of ids => Need to ask
  ids: string[]
  type: 'performers' | 'exhibitors'
}


export const getBrandingData = ({ acc, accounts, event, apps, brandingSelectData, publishInfos }: { acc?: Partial<JoiAccount>, 
  accounts?: { [id: string]: Partial<JoiAccount> }, event?: JoiEvent, publishInfos?: { [id:string]: PublishInfo }, 
  apps?: { [id: string]: JoiAppInfo }, brandingSelectData: BrandingSelectData }): BrandingData => {

  // account needed for forms & publish & app
  // accounts needed for list
  // event needed for forms
  // publishInfos needed for publish
  // eventListInfos needed for list
  // apps needed for app

  const { type, publishId, accountId, brandFormType, eventListId, appId } = brandingSelectData
  // console.log({ acc, accounts, event, publishInfos, brandingSelectData, type, brandFormType, appId })

  let account: Partial<JoiAccount>
  switch (type) {
    case 'forms':
    case 'publish':
    case 'app':
      account = acc
      break
    case 'list':
      account = accounts[accountId]
      break
  }

  if (!account || (type === 'forms' && !event)) return null
  if (type === 'forms' && event.ignoredForms.includes(brandFormType)) return null

  const joiTemplate = getJoiTemplate()

  const emptyHeaders = createJoiBrandImageSet('headers', 'Empty Header Image Set')
  emptyHeaders.id = 'empty'

  const emptyFooters = createJoiBrandImageSet('footers', 'Empty Footer Image Set')
  emptyFooters.id = 'empty'

  const emptySocialLinks = makeNewSocialTemplate('Empty Social Links Set')
  emptySocialLinks.id = 'empty'

  const emptyFooterLinks = makeNewFooterLinks('Empty Footer Links Set')
  emptyFooterLinks.id = 'empty'


  // console.log({ account })
  // THIS IS EASIER THAN ADDING LOTS OF CHECKING BELOW
  account = jsonCopyObject(account) // make sure not to mutate and overwrite inadverntly
  if (!account.templates) account.templates = []
  if (!account.headers) account.headers = []
  if (!account.socialTemplates) account.socialTemplates = []
  if (!account.footerLinks) account.footerLinks = []
  if (!account.footers) account.footers = []

  const possibleTemplates: JoiTemplate[] = [ getJoiTemplate(), account.templates.find(t => t.id === account.templateId) ]
  const possibleHeaders: JoiBrandImage[] = [ account.headerId === 'empty' ? emptyHeaders : account.headers.find(h => h.id === account.headerId) ]
  const possibleFooters: JoiBrandImage[] = [ account.footerId === 'empty' ? emptyFooters : account.footers.find(f => f.id === account.footerId) ]
  const possibleSocialTemplates: SocialTemplate[] = [ account.socialId === 'empty' ? emptySocialLinks : account.socialTemplates.find(t => t.id === account.socialId) ]
  const possibleFooterLinks: FooterLinks[] = [ account.footerLinksId === 'empty' ? emptyFooterLinks : account.footerLinks.find(fl => fl.id === account.footerLinksId) ]

  switch (type) {
    case 'app':
      const app = apps[appId]
      possibleTemplates.push(app.templateId === 'joi' ? joiTemplate : account.templates.find(t => t.id === app.templateId))
      possibleHeaders.push(app.headerId === 'empty' ? emptyHeaders : account.headers.find(t => t.id === app.headerId))
      possibleFooters.push(app.footerId === 'empty' ? emptyFooters : account.footers.find(t => t.id === app.footerId))
      possibleSocialTemplates.push(app.socialId === 'empty' ? emptySocialLinks : account.socialTemplates.find(s => s.id === app.socialId))
      possibleFooterLinks.push(app.footerLinksId === 'empty' ? emptyFooterLinks : account.footerLinks.find(fl => fl.id === app.footerLinksId))
      break
    case 'forms':
      possibleTemplates.push(event.templateId === 'joi' ? joiTemplate : account.templates.find(t => t.id === event.templateId))
      possibleHeaders.push(event.headerId === 'empty' ? emptyHeaders : account.headers.find(t => t.id === event.headerId))
      possibleFooters.push(event.footerId === 'empty' ? emptyFooters : account.footers.find(t => t.id === event.footerId))
      possibleSocialTemplates.push(event.socialId === 'empty' ? emptySocialLinks : account.socialTemplates.find(s => s.id === event.socialId))
      possibleFooterLinks.push(event.footerLinksId === 'empty' ? emptyFooterLinks : account.footerLinks.find(fl => fl.id === event.footerLinksId))
      break
    case 'publish':
      const publishInfo = publishInfos[publishId]
      possibleTemplates.push(publishInfo?.templateId === 'joi' ? joiTemplate : account.templates.find(t => t.id === publishInfo?.templateId))
      possibleHeaders.push(publishInfo?.headerId === 'empty' ? emptyHeaders : account.headers.find(t => t.id === publishInfo?.headerId))
      possibleFooters.push(publishInfo?.footerId === 'empty' ? emptyFooters : account.footers.find(t => t.id === publishInfo?.footerId))
      possibleSocialTemplates.push(publishInfo?.socialId === 'empty' ? emptySocialLinks : account.socialTemplates.find(s => s.id === publishInfo?.socialId))
      possibleFooterLinks.push(publishInfo?.footerLinksId === 'empty' ? emptyFooterLinks : account.footerLinks.find(fl => fl.id === publishInfo?.footerLinksId))
      break
    case 'list':
      const eventListInfo = account.eventListInfos[eventListId]
      if (!eventListInfo) {
        console.error("N event list info for ", { brandingSelectData })
        return null
      }
      possibleTemplates.push(eventListInfo?.templateId === 'joi' ? joiTemplate : account.templates.find(t => t.id === eventListInfo?.templateId))
      possibleHeaders.push(eventListInfo?.headerId === 'empty' ? emptyHeaders : account.headers.find(t => t.id === eventListInfo?.headerId))
      possibleFooters.push(eventListInfo?.footerId === 'empty' ? emptyFooters : account.footers.find(t => t.id === eventListInfo?.footerId))
      possibleSocialTemplates.push(eventListInfo?.socialId === 'empty' ? emptySocialLinks : account.socialTemplates.find(s => s.id === eventListInfo?.socialId))
      possibleFooterLinks.push(eventListInfo?.footerLinksId === 'empty' ? emptyFooterLinks : account.footerLinks.find(fl => fl.id === eventListInfo?.footerLinksId))
      break
    default:
      console.log("NO HANDLING FOR BRAND DATA RESELECTOR FOR TYPE " + type)
      return null
  }

  const template = getLastTruthyInList(possibleTemplates)
  const header = getLastTruthyInList(possibleHeaders)
  const footer = getLastTruthyInList(possibleFooters)
  const social = getLastTruthyInList(possibleSocialTemplates)
  const footerLinks = getLastTruthyInList(possibleFooterLinks)

  const brandingData: BrandingData = {
    template: template,
    header,
    footer,
    social,
    footerLinks
  }

  return brandingData
}

export const getSelectableLabelGroups = (labelIds: Set<string>, labels: LabelObject): SelectableLabelGroup[] => 
  !labelIds.size ? null : [{
    name: 'LABELS',
    labels: Array.from(labelIds).filter(id => labels[id]).map(id => ({ ...labels[id] })),
  }]